import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import App from './App';
import './index.css';
import { ThemeProvider } from './theme/ThemeProvider';
import { I18nProvider } from './i18n/I18nProvider';
import { applyTheme, DEFAULT_THEME_ID, readStoredThemeId } from './theme/themes';
import { applyLayout, getAutoLayoutId, readForcedLayoutId } from './layout/layouts';
import { LayoutProvider } from './layout/LayoutProvider';
import { applyTypography, DEFAULT_TYPOGRAPHY_ID, readStoredTypographyId } from './typography/typography';
import { TypographyProvider } from './typography/TypographyProvider';
import { isVerboseLoggingEnabled, logger } from './utils/logger';
import { SoundProvider } from './context/SoundContext';
import { AuthProvider } from './context/AuthContext';
import { appMetadata, getBuildVersion } from './utils/buildVersion';
import { resolveOnlineTransportConfig } from './utils/onlineTransport';

const buildVersion = getBuildVersion();
const isNativePlatform = () => Capacitor.isNativePlatform?.() ?? false;
const onlineTransportConfig = resolveOnlineTransportConfig();

const initialThemeId = (() => {
  if (typeof document === 'undefined') return DEFAULT_THEME_ID;
  const stored = readStoredThemeId();
  const themeId = stored ?? DEFAULT_THEME_ID;
  applyTheme(themeId);
  return themeId;
})();

(() => {
  if (typeof document === 'undefined') return getAutoLayoutId();
  const layoutId = readForcedLayoutId() ?? getAutoLayoutId();
  applyLayout(layoutId);
  return layoutId;
})();

const initialTypographyId = (() => {
  if (typeof document === 'undefined') return DEFAULT_TYPOGRAPHY_ID;
  const typographyId = readStoredTypographyId() ?? DEFAULT_TYPOGRAPHY_ID;
  applyTypography(typographyId);
  return typographyId;
})();

// Allow wymuszanie logów przez globalne flagi, ale nie spamujemy konsoli automatycznie
if (typeof window !== 'undefined') {
  try {
    window.__APP_METADATA__ = {
      ...appMetadata,
      onlineTransport: {
        transport: 'PARTYKIT',
        partyKitHost: onlineTransportConfig.partyKitHost,
      },
    };
  } catch {}
  if (typeof window.__DICE_DEBUG__ === 'undefined') {
    window.__DICE_DEBUG__ = false;
  }

  if (isVerboseLoggingEnabled()) {
    logger.info('DEBUG_MODE', {
      diceLogs: true,
      bannerLogs: true,
      turnStateLogs: true,
      timerLogs: true,
    });
  }

  // Scenario Launcher: jeśli __SCENARIO_SEED__ jest ustawiony, podmień WebSocket na mock
  // który odpowie LOBBY_STATE + SNAPSHOT z seeda przy pierwszym REQUEST_STATE
  if (window.__SCENARIO_SEED__) {
    const seed = window.__SCENARIO_SEED__;
    class MockScenarioWebSocket extends EventTarget {
      static readonly CONNECTING = 0;
      static readonly OPEN = 1;
      static readonly CLOSING = 2;
      static readonly CLOSED = 3;
      readyState = MockScenarioWebSocket.CONNECTING;
      url: string;
      protocol = '';
      extensions = '';
      binaryType: BinaryType = 'blob';
      bufferedAmount = 0;
      onopen: ((event: Event) => void) | null = null;
      onmessage: ((event: MessageEvent<string>) => void) | null = null;
      onclose: ((event: CloseEvent) => void) | null = null;
      onerror: ((event: Event) => void) | null = null;
      constructor(url: string | URL) {
        super();
        this.url = String(url);
        this.readyState = MockScenarioWebSocket.OPEN;
        setTimeout(() => {
          const openEvent = new Event('open');
          this.dispatchEvent(openEvent);
          this.onopen?.(openEvent);
        }, 50);
      }
      send(raw: string) {
        let parsed: { type?: string } | null;
        try { parsed = JSON.parse(raw) as { type?: string }; } catch { parsed = null; }
        if (parsed?.type !== 'REQUEST_STATE') return;
        if (seed.lobbyState) {
          const lobbyEvent = new MessageEvent('message', {
            data: JSON.stringify({ type: 'LOBBY_STATE', state: seed.lobbyState }),
          });
          setTimeout(() => { this.dispatchEvent(lobbyEvent); this.onmessage?.(lobbyEvent); }, 0);
        }
        if (seed.snapshot) {
          const snapshotEvent = new MessageEvent('message', {
            data: JSON.stringify({ type: 'SNAPSHOT', snapshot: seed.snapshot }),
          });
          setTimeout(() => { this.dispatchEvent(snapshotEvent); this.onmessage?.(snapshotEvent); }, 0);
        }
      }
      close() {
        this.readyState = MockScenarioWebSocket.CLOSED;
        const closeEvent = new CloseEvent('close', { code: 1000, reason: 'mock-close' });
        this.dispatchEvent(closeEvent);
        this.onclose?.(closeEvent);
      }
    }
    window.WebSocket = MockScenarioWebSocket as typeof window.WebSocket;
  }
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    {/* Opt-in do zachowań planowanych na v7 aby zniknęły ostrzeżenia future flag */}
    <I18nProvider>
      <LayoutProvider>
        <ThemeProvider initialThemeId={initialThemeId}>
          <TypographyProvider initialTypographyId={initialTypographyId}>
            <SoundProvider>
              <AuthProvider>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </AuthProvider>
            </SoundProvider>
          </TypographyProvider>
        </ThemeProvider>
      </LayoutProvider>
    </I18nProvider>
  </React.StrictMode>
);

// Rejestracja service workera (PWA)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // W dev i na platformach native blokujemy SW (i czyścimy stare), bo może łapać cache i psuć websockety
    if (import.meta.env.DEV || isNativePlatform()) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => reg.unregister());
      }).catch(err => logger.warn('SW_UNREGISTER_FAILED', err));
      return;
    }

    const swPath = `/sw.js?v=${encodeURIComponent(buildVersion)}`;
    navigator.serviceWorker.register(swPath).catch(err => {
      logger.warn('SW_REGISTRATION_FAILED', err);
    });
  });
}
